import React from "react";
import { getPlanDetails } from "../utils/premiumHelpers";

export default function PricingModal({ isOpen, onClose, onSelectPlan }) {
  if (!isOpen) return null;

  const plans = [
    { id: "1-month", recommended: false },
    { id: "3-months", recommended: true },
    { id: "6-months", recommended: false },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white p-6 flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold">Go Premium</h2>
            <p className="text-indigo-100 mt-1">Unlock exclusive features and see contact details</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-indigo-700 rounded-full p-2 transition"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Features List */}
          <div className="mb-12">
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-6">Premium Benefits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="text-2xl">👁️</div>
                <div>
                  <p className="font-semibold text-gray-800 dark:text-white">See Contact Details</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">View phone numbers of other users</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="text-2xl">📱</div>
                <div>
                  <p className="font-semibold text-gray-800 dark:text-white">Share Your Contact</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Let others see your information</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="text-2xl">⭐</div>
                <div>
                  <p className="font-semibold text-gray-800 dark:text-white">Premium Badge</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Stand out from the crowd</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="text-2xl">⏱️</div>
                <div>
                  <p className="font-semibold text-gray-800 dark:text-white">Flexible Plans</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Choose 1, 3, or 6 months</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((planItem) => {
              const plan = getPlanDetails(planItem.id);
              return (
                <div
                  key={planItem.id}
                  className={`relative rounded-xl border-2 p-6 transition-all ${
                    planItem.recommended
                      ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 shadow-lg scale-105"
                      : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700"
                  }`}
                >
                  {/* Recommended Badge */}
                  {planItem.recommended && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-indigo-600 text-white px-4 py-1 rounded-full text-sm font-bold whitespace-nowrap">
                        ⭐ Recommended
                      </span>
                    </div>
                  )}

                  {/* Badge */}
                  {plan.badge && (
                    <div className="mb-3">
                      <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-xs font-bold">
                        {plan.badge}
                      </span>
                    </div>
                  )}

                  {/* Plan Name */}
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">{plan.name}</h3>

                  {/* Price */}
                  <div className="mb-4">
                    <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                      ₹{plan.price.toLocaleString()}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      ₹{plan.monthlyRate.toLocaleString()}/month
                    </p>
                  </div>

                  {/* Duration */}
                  <div className="mb-6 p-3 bg-gray-100 dark:bg-gray-600 rounded-lg">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>{plan.durationMonths}</strong> month{plan.durationMonths > 1 ? "s" : ""} of access
                    </p>
                  </div>

                  {/* Button */}
                  <button
                    onClick={() => onSelectPlan(planItem.id)}
                    className={`w-full font-bold py-3 rounded-lg transition ${
                      planItem.recommended
                        ? "bg-indigo-600 text-white hover:bg-indigo-700"
                        : "bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-500"
                    }`}
                  >
                    Choose Plan
                  </button>

                  {/* Terms */}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center">
                    One-time payment. No auto-renewal.
                  </p>
                </div>
              );
            })}
          </div>

          {/* Footer Note */}
          <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-900 dark:text-blue-200">
              <strong>💡 Tip:</strong> The 3-month plan offers the best value with 50% savings compared to monthly
              billing!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
